/**
 * Life Event Probability Engine v2
 * Analyzes natal chart + dasha periods + transits to predict likelihood of events
 *
 * v2 improvements:
 *  - Extended LifeEventType: investment, foreign_travel, marriage
 *  - Natural karaka scoring per event
 *  - Functional nature weighting (benefic x1.2-1.3, malefic x0.7-0.9)
 *  - Dasha lord dignity modifiers (exalted x1.3, debil/combust x0.7-0.75)
 *  - All relevant house lords checked at every dasha level
 *  - Rahu/Ketu activity-aware transit scoring
 *  - Double transit rule: Jupiter+Saturn same house → +20
 *  - Sade Sati suppressor
 *  - Activity-specific Mercury/Sun/Mars/Venus transits
 *  - Supabase interpretation text keyword scoring
 *  - Multi-varga analysis (D4+D9, D10+D9, D7+D9, etc.)
 *  - Fixed planets parameter (was astroData.planets, now astroData.placements)
 */
import * as Astronomy from 'astronomy-engine';
import type { ActivityType } from '../muhurta/muhurtaEngine';
import { getHouseLord, SIGN_LORDS } from '../../utils/lordshipUtils';
import type { PlanetName } from '../../utils/astroEngine';

// Extended activity set — includes muhurta activities + predictive-only events
export type LifeEventType = ActivityType | 'investment' | 'foreign_travel' | 'marriage';

// ─────────────────────────────────────────────────
// STATIC LOOKUP TABLES
// ─────────────────────────────────────────────────

const ACTIVITY_HOUSES: Record<LifeEventType, number[]> = {
  // H10=profession, H1=self/native, H11=gains/salary, H6=service/employment,
  // H3=initiative/self-effort (business angle), H2=wealth/income
  business_start:    [10, 1, 11, 6, 3, 2],  // H6 co-primary for job/service; H3 for entrepreneurship
  property_purchase: [4,  11, 1, 2, 12],    // H1 idx=2: the buyer activates the acquisition
  graha_pravesh:     [4,  2,  1, 12],       // H1 idx=2: the native enters their own home
  vehicle_purchase:  [4,  11, 2],           // H4 dominates conveyances; H1 tertiary, skip
  surgery:           [6,  1,  8],           // H1 idx=1: vitality/body of the native undergoing surgery
  ivf_planning:      [5,  7,  1, 8],        // H1 idx=2: the native's body + desire
  cesarean:          [5,  1,  8, 4],        // H1 idx=1: mother's body/vitality is co-primary
  investment:        [2,  11, 1, 5, 9],     // H1 idx=2: investor's judgment and initiative
  foreign_travel:    [9,  12, 1, 3, 7],     // H1 idx=2: the traveler who journeys
  marriage:          [7,  1,  2, 11, 5],    // H1 co-primary: H1-H7 is the Parashara marriage axis
};

const ACTIVITY_KARAKAS: Record<LifeEventType, string[]> = {
  // Saturn governs service/karma — classical trigger for job changes/promotions
  business_start:    ['Mercury', 'Sun', 'Mars', 'Jupiter', 'Saturn'],
  property_purchase: ['Mars',    'Moon',    'Saturn'],
  graha_pravesh:     ['Moon',    'Mars',    'Venus'],
  vehicle_purchase:  ['Venus',   'Mars',    'Mercury'],
  surgery:           ['Mars',    'Saturn',  'Sun'],
  ivf_planning:      ['Jupiter', 'Venus',   'Moon'],
  cesarean:          ['Jupiter', 'Moon',    'Mars'],
  investment:        ['Jupiter', 'Venus',   'Mercury'],
  foreign_travel:    ['Jupiter', 'Rahu',    'Saturn'],
  marriage:          ['Venus',   'Jupiter', 'Moon'],
};

const ACTIVITY_FAVORABLE_PLANETS: Record<LifeEventType, string[]> = {
  // Saturn added: service/employment karaka; Jupiter for career growth/promotion
  business_start:    ['Mercury', 'Jupiter', 'Sun', 'Venus', 'Mars', 'Saturn'], // Saturn for career/service
  property_purchase: ['Mars',    'Venus',   'Jupiter', 'Moon'],
  graha_pravesh:     ['Venus',   'Jupiter', 'Moon',    'Mercury'],
  vehicle_purchase:  ['Venus',   'Mercury', 'Mars'],
  surgery:           ['Sun',     'Mars'],
  ivf_planning:      ['Jupiter', 'Venus',   'Moon'],
  cesarean:          ['Jupiter', 'Moon',    'Venus'],
  investment:        ['Jupiter', 'Venus',   'Mercury', 'Sun'],
  foreign_travel:    ['Jupiter', 'Rahu',    'Saturn'],
  marriage:          ['Venus',   'Jupiter', 'Moon',    'Mars'],
};

// Varga charts to check per event type (primary first, then D9 always)
const ACTIVITY_VARGAS: Record<LifeEventType, string[]> = {
  business_start:    ['D10', 'D9'],
  property_purchase: ['D4',  'D9'],
  graha_pravesh:     ['D4',  'D9'],
  vehicle_purchase:  ['D4',  'D9'],
  surgery:           ['D8',  'D9'],
  ivf_planning:      ['D7',  'D9'],
  cesarean:          ['D7',  'D9'],
  investment:        ['D2',  'D9'],
  foreign_travel:    ['D9',  'D12'],
  marriage:          ['D9',  'D7'],
};

// Event-relevant keywords for interpretation text scoring
const ACTIVITY_INTERP_KEYWORDS: Record<LifeEventType, string[]> = {
  business_start:    ['business', 'career', 'success', 'profit', 'profession', 'trade', 'entrepreneurship', 'enterprise', 'service', 'employment', 'job', 'promotion', 'occupation', 'income'],
  property_purchase: ['property', 'house', 'land', 'real estate', 'home', 'purchase'],
  graha_pravesh:     ['home', 'property', 'comfort', 'residence', 'domestic'],
  vehicle_purchase:  ['vehicle', 'transport', 'travel', 'comfort', 'luxury'],
  surgery:           ['health', 'surgery', 'disease', 'recovery', 'medical', 'healing'],
  ivf_planning:      ['children', 'fertility', 'conception', 'progeny', 'offspring'],
  cesarean:          ['children', 'birth', 'progeny', 'delivery', 'motherhood'],
  investment:        ['wealth', 'gains', 'investment', 'income', 'financial', 'prosperity'],
  foreign_travel:    ['foreign', 'travel', 'abroad', 'journey', 'overseas', 'settlement'],
  marriage:          ['marriage', 'spouse', 'partner', 'love', 'relationship', 'wedding'],
};

// Planet sign dignities (0-indexed sign 0-11)
const PLANET_EXALT_SIGN: Partial<Record<string, number>> = {
  Sun: 0, Moon: 1, Mars: 9, Mercury: 5, Jupiter: 3, Venus: 11, Saturn: 6,
};
const PLANET_DEBIL_SIGN: Partial<Record<string, number>> = {
  Sun: 6, Moon: 7, Mars: 3, Mercury: 11, Jupiter: 9, Venus: 5, Saturn: 0,
};
const PLANET_OWN_SIGNS: Partial<Record<string, number[]>> = {
  Sun: [4], Moon: [3], Mars: [0, 7], Mercury: [2, 5],
  Jupiter: [8, 11], Venus: [1, 6], Saturn: [9, 10],
};

// ─────────────────────────────────────────────────
// INTERFACES
// ─────────────────────────────────────────────────

export interface ProbabilityWindow {
  startDate: Date;
  endDate: Date;
  probability: number;
  rating: 'Low' | 'Medium' | 'High' | 'Very High';
  natalScore: number;
  dashaScore: number;
  transitScore: number;
  dashaPeriods: { MD: string; AD: string; PD: string; SD?: string };
  supportingFactors: string[];
  challengingFactors: string[];
  keyTransits: string[];
}

export interface NatalProbabilityInput {
  activity: LifeEventType;
  houseScores?: Record<number, number>;
  bhavaBala?: Record<number, number>;
  planets?: Record<string, { house: number; sign: number; strength?: number; longitude?: number }>;
  ascendantSign: number;
  vargaData?: any;
  yogas?: any[];
  functionalNature?: Record<string, string>;
  debil?: Record<string, boolean>;
  exalt?: Record<string, boolean>;
  houseInterpretations?: Array<{
    house: number;
    planets?: Array<{ planet: string; interpretation?: string }>;
    lordship?: Array<{ lord: string; position: string; interpretation?: string; overview?: string }>;
  }>;
}

export interface DashaPeriod {
  planet: string;
  startDate: Date;
  endDate: Date;
  level: 'MD' | 'AD' | 'PD' | 'SD';
}

// ─────────────────────────────────────────────────
// PRIVATE HELPERS
// ─────────────────────────────────────────────────

function getPlanetDignityFromSign(planet: string, sign0: number): 'exalted' | 'own' | 'debilitated' | 'neutral' {
  if (PLANET_EXALT_SIGN[planet] === sign0) return 'exalted';
  if (PLANET_DEBIL_SIGN[planet] === sign0) return 'debilitated';
  if (PLANET_OWN_SIGNS[planet]?.includes(sign0)) return 'own';
  return 'neutral';
}

function getHouseName(house: number): string {
  const names: Record<number, string> = {
    1: '1st (Self)', 2: '2nd (Wealth)', 3: '3rd (Courage)', 4: '4th (Property)',
    5: '5th (Children)', 6: '6th (Health)', 7: '7th (Partnership)',
    8: '8th (Transformation)', 9: '9th (Fortune)', 10: '10th (Career)',
    11: '11th (Gains)', 12: '12th (Expenses)',
  };
  return names[house] || `${house}th`;
}

function getSignElement(sign: number): string {
  const elements: Record<number, string> = {
    1: 'Fire', 2: 'Earth', 3: 'Air', 4: 'Water',
    5: 'Fire', 6: 'Earth', 7: 'Air', 8: 'Water',
    9: 'Fire', 10: 'Earth', 11: 'Air', 12: 'Water',
  };
  return elements[sign] || 'Unknown';
}

function isFriendly(planet1: PlanetName, planet2: PlanetName): boolean {
  const f: Partial<Record<string, string[]>> = {
    Sun: ['Moon', 'Mars', 'Jupiter'], Moon: ['Sun', 'Mercury'],
    Mars: ['Sun', 'Moon', 'Jupiter'], Mercury: ['Sun', 'Venus'],
    Jupiter: ['Sun', 'Moon', 'Mars'], Venus: ['Mercury', 'Saturn'],
    Saturn: ['Mercury', 'Venus'],
  };
  return f[planet1]?.includes(planet2) ?? false;
}

// ─────────────────────────────────────────────────
// NATAL PROBABILITY
// ─────────────────────────────────────────────────

export function calculateNatalProbability(input: NatalProbabilityInput): {
  score: number;
  factors: string[];
} {
  const {
    activity, houseScores, bhavaBala, planets, ascendantSign,
    vargaData, yogas, functionalNature, debil, exalt, houseInterpretations,
  } = input;

  const act = activity as LifeEventType;
  const relevantHouses = ACTIVITY_HOUSES[act] ?? ACTIVITY_HOUSES['business_start'];
  const favorablePlanets = ACTIVITY_FAVORABLE_PLANETS[act] ?? [];
  const karakas = ACTIVITY_KARAKAS[act] ?? [];

  let score = 0;
  const factors: string[] = [];

  // 1. House Scores
  {
    const scores = houseScores || bhavaBala;
    if (scores) {
      let total = 0;
      relevantHouses.forEach(h => { total += scores[h] ?? 0; });
      const avg = total / relevantHouses.length;
      const norm = houseScores ? avg : avg - 50;
      score += (norm / 100) * 12;
      relevantHouses.forEach(h => {
        const v = scores[h] ?? 0;
        const eff = houseScores ? v : v - 50;
        if (eff > 30) factors.push(`Strong ${getHouseName(h)} (${v > 0 ? '+' : ''}${Math.round(v)})`);
        else if (eff < -20) factors.push(`Weak ${getHouseName(h)} (${Math.round(v)})`);
      });
    }
  }

  // 2. Favorable planet placements
  if (planets) {
    const primaryH = relevantHouses[0];
    let bonus = 0;
    favorablePlanets.forEach(pl => {
      const d = planets[pl];
      if (!d) return;
      const fnBoost = functionalNature?.[pl] === 'yoga_karaka' ? 1.5
                    : functionalNature?.[pl] === 'benefic'    ? 1.3
                    : functionalNature?.[pl] === 'malefic'    ? 0.7 : 1.0;
      if (d.house === primaryH) {
        bonus += 5 * fnBoost;
        factors.push(`${pl} in ${getHouseName(primaryH)} (excellent)`);
      } else if (relevantHouses.includes(d.house)) {
        bonus += 3 * fnBoost;
        factors.push(`${pl} in ${getHouseName(d.house)} (supportive)`);
      }
      if (d.strength && d.strength > 70) { bonus += 2; factors.push(`${pl} strong (${Math.round(d.strength)}%)`); }
    });
    score += Math.min(bonus, 15);
  }

  // 2b. Planetary conjunctions in relevant houses (favorable planets + karakas)
  if (planets) {
    const housePlanets: Record<number, string[]> = {};
    [...new Set([...favorablePlanets, ...karakas])].forEach(pl => {
      const d = planets[pl];
      if (!d) return;
      if (!housePlanets[d.house]) housePlanets[d.house] = [];
      if (!housePlanets[d.house].includes(pl)) housePlanets[d.house].push(pl);
    });
    let conjBonus = 0;
    relevantHouses.forEach(h => {
      const together = housePlanets[h];
      if (!together || together.length < 2) return;
      const pts = together.length >= 3 ? 8 : 5;
      conjBonus += pts;
      factors.push(`Conjunction ${together.join('+')} in ${getHouseName(h)} (+${pts})`);
    });
    score += Math.min(conjBonus, 8);
  }

  // 2c. Yoga Karaka in relevant house (not already counted in Section 2)
  // A YK placed in a relevant house energises that house's life themes powerfully,
  // even when it is not the primary karaka for this event type.
  if (planets && functionalNature) {
    const ykBonus = Object.entries(functionalNature)
      .filter(([, v]) => v === 'yoga_karaka')
      .reduce((acc, [pl]) => {
        if (favorablePlanets.includes(pl)) return acc; // already counted in Section 2
        const d = planets[pl];
        if (!d) return acc;
        if (d.house === relevantHouses[0]) {
          factors.push(`${pl} (Yoga Karaka) in ${getHouseName(relevantHouses[0])} — activates primary domain`);
          return acc + 7;
        } else if (relevantHouses.includes(d.house)) {
          factors.push(`${pl} (Yoga Karaka) in ${getHouseName(d.house)} — supports event`);
          return acc + 4;
        }
        return acc;
      }, 0);
    score += Math.min(ykBonus, 10);
  }

  // 3. Natural Karakas (capped to prevent single section dominating)
  if (planets) {
    let karakaBonus = 0;
    karakas.forEach((karaka, idx) => {
      const d = planets[karaka];
      if (!d) return;
      const weight = idx === 0 ? 8 : idx === 1 ? 5 : idx === 2 ? 3 : 2;
      const fnMult = functionalNature?.[karaka] === 'yoga_karaka' ? 1.4
                   : functionalNature?.[karaka] === 'benefic'    ? 1.2
                   : functionalNature?.[karaka] === 'malefic'    ? 0.8 : 1.0;
      if (relevantHouses.includes(d.house)) {
        karakaBonus += Math.round(weight * fnMult);
        factors.push(`${karaka} (karaka) in ${getHouseName(d.house)}`);
      }
      if (exalt?.[karaka]) {
        karakaBonus += Math.round(weight * 0.4 * fnMult);
        factors.push(`${karaka} exalted (karaka strong)`);
      } else if (debil?.[karaka]) {
        score -= Math.round(weight * 0.4); // Debilitation still penalises directly
        factors.push(`${karaka} debilitated (karaka weakened)`);
      }
    });
    score += Math.min(karakaBonus, 14);
  }

  // 4. Ascendant element
  const ascEl = getSignElement(ascendantSign);
  if (act === 'business_start' && ['Air', 'Fire'].includes(ascEl)) { score += 5; factors.push(`${ascEl} ascendant (entrepreneurial)`); }
  if (act === 'property_purchase' && ['Earth', 'Water'].includes(ascEl)) { score += 5; factors.push(`${ascEl} ascendant (stability)`); }
  if (act === 'marriage' && ['Earth', 'Water'].includes(ascEl)) { score += 4; factors.push(`${ascEl} ascendant (relationship)`); }

  // 5. House Lord placements (capped to prevent all-lords-strong inflating score)
  if (planets) {
    let lordBonus = 0;
    relevantHouses.forEach((house, idx) => {
      const lord = getHouseLord(house as any, ascendantSign);
      const d = planets[lord];
      if (!d) return;
      const fnMult = functionalNature?.[lord] === 'yoga_karaka' ? 1.4
                   : functionalNature?.[lord] === 'benefic'    ? 1.2
                   : functionalNature?.[lord] === 'malefic'    ? 0.85 : 1.0;
      const w = idx === 0 ? 5 : 3;
      if ([1,2,4,5,9,10,11].includes(d.house)) {
        lordBonus += Math.round(w * fnMult);
        factors.push(`${lord} (${getHouseName(house)} lord) in ${getHouseName(d.house)}`);
      } else if ([6,8,12].includes(d.house)) {
        score -= idx === 0 ? 5 : 2; // Dusthana penalty still goes direct
        factors.push(`${lord} (lord) in dusthana ${getHouseName(d.house)}`);
      }
    });
    score += Math.min(lordBonus, 10);
  }

  // 6. Multi-Varga analysis
  if (vargaData && planets) {
    const vargas = ACTIVITY_VARGAS[act] ?? ['D9'];
    const primaryLord = getHouseLord(relevantHouses[0] as any, ascendantSign);
    vargas.forEach(varga => {
      const chart = vargaData[varga];
      if (!chart?.planetSigns?.[primaryLord]) return;
      const vSign = chart.planetSigns[primaryLord];
      const sign0 = ((vSign - 1) + 12) % 12;
      const dignity = getPlanetDignityFromSign(primaryLord, sign0);
      if (dignity === 'exalted') {
        score += 10; factors.push(`${primaryLord} exalted in ${varga}`);
      } else if (dignity === 'own') {
        score += 8; factors.push(`${primaryLord} own sign in ${varga}`);
      } else if (dignity === 'debilitated') {
        score -= 5; factors.push(`${primaryLord} debilitated in ${varga}`);
      } else {
        const vLord = SIGN_LORDS[vSign];
        if (vLord && isFriendly(vLord as PlanetName, primaryLord as PlanetName)) {
          score += 5; factors.push(`${primaryLord} friendly sign in ${varga}`);
        }
      }
    });
  }

  // 7. Classical Yogas
  if (yogas?.length) {
    const kws = ACTIVITY_INTERP_KEYWORDS[act];
    const rel = yogas.filter((y: any) =>
      kws.some(kw => [y.ruleKey, y.type, y.name, y.message].some(
        (f: any) => typeof f === 'string' && f.toLowerCase().includes(kw)
      ))
    );
    if (rel.length > 0) {
      score += Math.min(rel.length * 4, 8);
      factors.push(`${rel.length} relevant yoga(s) active`);
    }
  }

  // 8. Interpretation text keyword scoring
  if (houseInterpretations) {
    const kws = ACTIVITY_INTERP_KEYWORDS[act];
    let interpBonus = 0;
    relevantHouses.forEach(h => {
      const hi = houseInterpretations.find((x: any) => x.house === h);
      if (!hi) return;
      (hi.planets ?? []).forEach((p: any) => {
        // Scan all available text fields: overview, nakshatra note, house-planet note
        const txt = [
          p.interpretation ?? '',
          p.nakshatraNote ?? '',
          p.housePlanetNote ?? '',
        ].join(' ').toLowerCase();
        const matchedKws = kws.filter(kw => txt.includes(kw));
        if (matchedKws.length > 0) {
          const pts = matchedKws.length >= 2 ? 4 : 3;
          interpBonus += pts;
          factors.push(`${p.planet} in ${getHouseName(h)}: prediction supports ${act.replace(/_/g, ' ')}`);
        }
      });
      (hi.lordship ?? []).forEach((l: any) => {
        const txt = [
          l.interpretation ?? '',
          l.overview ?? '',
          l.strengths ?? '',
        ].join(' ').toLowerCase();
        const matchedKws = kws.filter(kw => txt.includes(kw));
        if (matchedKws.length > 0) {
          const pts = matchedKws.length >= 2 ? 3 : 2;
          interpBonus += pts;
          factors.push(`${l.lord} (${l.position}) prediction supports this event`);
        }
      });
    });
    score += Math.min(interpBonus, 8);
  }

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    factors,
  };
}

// ─────────────────────────────────────────────────
// DASHA PROBABILITY
// ─────────────────────────────────────────────────

export function calculateDashaProbability(
  activity: LifeEventType | ActivityType,
  dashaPeriods: { MD: string; AD: string; PD: string; SD?: string },
  planets?: Record<string, { house: number; sign: number; longitude?: number }>,
  ascendantSign?: number,
  dignities?: {
    debil?: Partial<Record<string, boolean>>;
    exalt?: Partial<Record<string, boolean>>;
    combust?: Partial<Record<string, boolean>>;
    functionalNature?: Record<string, string>;
  }
): { score: number; factors: string[] } {
  const act = activity as LifeEventType;
  const { MD, AD, PD, SD } = dashaPeriods;
  const relevantHouses = ACTIVITY_HOUSES[act] ?? ACTIVITY_HOUSES['business_start'];
  const favorablePlanets = ACTIVITY_FAVORABLE_PLANETS[act] ?? [];
  const karakas = ACTIVITY_KARAKAS[act] ?? [];
  const fn = dignities?.functionalNature;

  let score = 0;
  const factors: string[] = [];

  // Dignity modifier for a dasha lord
  function dignityMod(planet: string): number {
    if (dignities?.combust?.[planet] || dignities?.debil?.[planet]) return 0.7;
    if (dignities?.exalt?.[planet]) return 1.3;
    const d = planets?.[planet];
    if (d?.sign !== undefined) {
      const s0 = ((d.sign - 1) + 12) % 12;
      const dig = getPlanetDignityFromSign(planet, s0);
      if (dig === 'exalted' || dig === 'own') return 1.2;
      if (dig === 'debilitated') return 0.75;
    }
    return fn?.[planet] === 'yoga_karaka' ? 1.2
         : fn?.[planet] === 'benefic'    ? 1.1
         : fn?.[planet] === 'malefic'    ? 0.9 : 1.0;
  }

  // ── Section 1: Dasha lord IS the house lord ────────────────────────────
  // Classical rule: events manifest primarily in the dasha of the relevant
  // house lord. AD (antardasha) is the strongest timing trigger.
  if (ascendantSign) {
    let lordBonus = 0;
    relevantHouses.forEach((house, hIdx) => {
      const primaryLord = getHouseLord(house as any, ascendantSign);
      const houseWeight = hIdx === 0 ? 1.0 : 0.55; // Primary house most important
      // AD is the workhorse of timing; MD sets the theme; PD refines; SD triggers
      const levels: Array<['MD'|'AD'|'PD'|'SD', number]> = [
        ['MD', 12], ['AD', 26], ['PD', 12], ['SD', 7]
      ];
      levels.forEach(([lvl, base]) => {
        const planet = dashaPeriods[lvl];
        if (!planet || planet !== primaryLord) return;
        const pts = Math.round(base * houseWeight * dignityMod(planet));
        lordBonus += pts;
        factors.push(`${planet} ${lvl} = ${getHouseName(house)} lord (+${pts})`);
      });
    });
    // Cap: prevents accumulation across many houses at many levels
    score += Math.min(lordBonus, 40);

    // ── Karaka dasha: natural significator period ──
    // e.g. Venus AD for marriage, Jupiter AD for children
    let karakaBonus = 0;
    karakas.forEach((karaka, ki) => {
      const karakaWeight = ki === 0 ? 10 : ki === 1 ? 6 : 4;
      (['MD', 'AD', 'PD'] as const).forEach(lvl => {
        const planet = dashaPeriods[lvl];
        if (planet !== karaka) return;
        const base = lvl === 'AD' ? karakaWeight : Math.round(karakaWeight * 0.55);
        karakaBonus += Math.round(base * dignityMod(planet));
        factors.push(`${planet} ${lvl} is natural karaka`);
      });
    });
    score += Math.min(karakaBonus, 12);
  }

  // ── Section 2: Favorable planet in dasha ──────────────────────────────
  // AD most significant, then PD, MD; SD as minor trigger
  let favBonus = 0;
  (['MD', 'AD', 'PD', 'SD'] as const).forEach(lvl => {
    const planet = dashaPeriods[lvl];
    if (!planet) return;
    if (favorablePlanets.includes(planet)) {
      const base = lvl === 'AD' ? 12 : lvl === 'PD' ? 8 : lvl === 'MD' ? 5 : 4;
      const pts = Math.round(base * dignityMod(planet));
      favBonus += pts;
      factors.push(`${planet} ${lvl} favorable (+${pts})`);
    } else if (fn?.[planet] === 'yoga_karaka') {
      // Yoga Karaka dasha is inherently auspicious even if not the primary event karaka.
      // Lower weight than a direct favorablePlanet match but not zero.
      const base = lvl === 'AD' ? 7 : lvl === 'PD' ? 5 : lvl === 'MD' ? 4 : 2;
      const pts = Math.round(base * dignityMod(planet));
      favBonus += pts;
      factors.push(`${planet} ${lvl} Yoga Karaka (+${pts})`);
    }
    // Weakened unfavorable dasha lord is a genuine impediment
    if ((dignities?.debil?.[planet] || dignities?.combust?.[planet]) && !favorablePlanets.includes(planet)) {
      const pen = lvl === 'AD' ? 8 : lvl === 'MD' ? 5 : 3;
      score -= pen;
      factors.push(`${planet} ${lvl} weakened (−${pen})`);
    }
  });
  score += Math.min(favBonus, 20);

  // ── Section 3: Dasha lords placed in relevant houses natally ──────────
  // A dasha lord sitting in the relevant house amplifies its activation
  if (planets) {
    let houseBonus = 0;
    [MD, AD, PD].forEach((planet, li) => {
      const d = planets[planet];
      if (!d) return;
      if (relevantHouses.includes(d.house)) {
        const base = li === 1 ? 10 : li === 2 ? 7 : 4; // AD lord most potent
        const pts = Math.round(base * dignityMod(planet));
        houseBonus += pts;
        factors.push(`${planet} activates ${getHouseName(d.house)} (+${pts})`);
      }
    });
    score += Math.min(houseBonus, 15);
  }

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    factors,
  };
}

// ─────────────────────────────────────────────────
// TRANSIT PROBABILITY
// ─────────────────────────────────────────────────

export function calculateTransitProbability(
  activity: LifeEventType | ActivityType,
  date: Date,
  ascendantSign: number,
  ayanamsa: number = 24.14,
  moonNatalSign?: number,
): { score: number; factors: string[] } {
  const act = activity as LifeEventType;
  const relevantHouses = ACTIVITY_HOUSES[act] ?? ACTIVITY_HOUSES['business_start'];
  const primaryHouse = relevantHouses[0];

  // Transit base = 0: every point earned from actual planetary positions
  let score = 0;
  const factors: string[] = [];

  const time = Astronomy.MakeTime(date);

  function sidLon(body: Astronomy.Body): number {
    const vec = Astronomy.GeoVector(body, time, true);
    const ecl = Astronomy.Ecliptic(vec);
    return ((ecl.elon - ayanamsa) % 360 + 360) % 360;
  }

  const sunLon = sidLon(Astronomy.Body.Sun);

  // Jupiter
  const jupLon   = sidLon(Astronomy.Body.Jupiter);
  const jupSign  = Math.floor(jupLon / 30) + 1;
  const jupHouse = ((jupSign - ascendantSign + 12) % 12) + 1;
  const jupCombust = Math.min(Math.abs(jupLon - sunLon), 360 - Math.abs(jupLon - sunLon)) < 10;
  const jupAspectH = [5, 7, 9].map(o => ((jupHouse + o - 2) % 12) + 1);

  if (jupHouse === primaryHouse) {
    score += jupCombust ? 10 : 20;
    factors.push(`Jupiter in ${getHouseName(primaryHouse)}${jupCombust ? ' (combust)' : ''}`);
  } else if (relevantHouses.includes(jupHouse)) {
    score += jupCombust ? 5 : 10;
    factors.push(`Jupiter in ${getHouseName(jupHouse)} (supportive${jupCombust ? ', combust' : ''})`);
  }
  jupAspectH.forEach(ah => {
    if (relevantHouses.includes(ah)) {
      score += jupCombust ? 3 : 6;
      factors.push(`Jupiter aspects ${getHouseName(ah)}`);
    }
  });

  // Saturn
  const satLon   = sidLon(Astronomy.Body.Saturn);
  const satSign  = Math.floor(satLon / 30) + 1;
  const satHouse = ((satSign - ascendantSign + 12) % 12) + 1;
  const satAspectH = [3, 7, 10].map(o => ((satHouse + o - 2) % 12) + 1);

  if (satHouse === primaryHouse) {
    score -= 15;
    factors.push(`Saturn in ${getHouseName(primaryHouse)} (delays)`);
  } else if (relevantHouses.includes(satHouse)) {
    score -= 8;
    factors.push(`Saturn in ${getHouseName(satHouse)} (restrictive)`);
  }
  satAspectH.forEach(ah => {
    if (relevantHouses.includes(ah)) {
      score -= 5;
      factors.push(`Saturn aspects ${getHouseName(ah)}`);
    }
  });

  // Double Transit Rule
  const jupAll = new Set([jupHouse, ...jupAspectH]);
  const satAll = new Set([satHouse, ...satAspectH]);
  relevantHouses.forEach(h => {
    if (jupAll.has(h) && satAll.has(h)) {
      score += 20;
      factors.push(`Double transit (Jupiter+Saturn) on ${getHouseName(h)}`);
    }
  });

  // Sade Sati
  if (moonNatalSign) {
    const sadeSatiSigns = [
      ((moonNatalSign - 2 + 12) % 12) + 1,
      moonNatalSign,
      (moonNatalSign % 12) + 1,
    ];
    if (sadeSatiSigns.includes(satSign)) {
      score -= 10;
      factors.push(`Sade Sati active (Saturn near natal Moon)`);
    }
  }

  // Rahu/Ketu — mean ascending node (same formula as astroEngine.getRahuKetuSidereal)
  const _T = (date.getTime() / 86400000 + 2440587.5 - 2451545.0) / 36525.0;
  const _omega = ((125.04455501 - 1934.1361849 * _T + 0.0020762 * _T * _T + (_T * _T * _T) / 467410 - (_T * _T * _T * _T) / 606160) % 360 + 360) % 360;
  const rahuLon  = ((_omega - ayanamsa) % 360 + 360) % 360;
  const rahuSign = Math.floor(rahuLon / 30) + 1;
  const rahuH    = ((rahuSign - ascendantSign + 12) % 12) + 1;
  const ketuH    = ((rahuH + 5) % 12) + 1;

  // Rahu in H6 favorable for business_start (career/competitive drive)
  const rahuFav: Partial<Record<LifeEventType, number[]>> = {
    business_start: [3, 6, 10, 11], property_purchase: [4, 11], graha_pravesh: [4, 1],
    vehicle_purchase: [4, 11], surgery: [6, 8], ivf_planning: [5, 1], cesarean: [5, 1],
    investment: [11, 2], foreign_travel: [9, 12, 3], marriage: [7, 11],
  };
  // Rahu in H6 now favorable for career/service (competitive drive); removed from adverse
  const rahuAdv: Partial<Record<LifeEventType, number[]>> = {
    business_start: [8, 12], property_purchase: [6, 8, 12], graha_pravesh: [6, 8],
    vehicle_purchase: [6, 8], surgery: [12, 5], ivf_planning: [8, 12], cesarean: [8, 12],
    investment: [6, 8, 12], foreign_travel: [6, 8], marriage: [6, 8, 12],
  };

  if ((rahuFav[act] ?? []).includes(rahuH)) {
    score += 8; factors.push(`Rahu in ${getHouseName(rahuH)} (amplifying)`);
  }
  if ((rahuAdv[act] ?? []).includes(rahuH)) {
    score -= 6; factors.push(`Rahu in adversarial ${getHouseName(rahuH)}`);
  }
  if ((rahuFav[act] ?? []).includes(ketuH)) {
    score += 4; factors.push(`Ketu in ${getHouseName(ketuH)} (spiritual nudge)`);
  }

  // Activity-specific planet transits
  const marsLon  = sidLon(Astronomy.Body.Mars);
  const marsH    = ((Math.floor(marsLon / 30) + 1 - ascendantSign + 12) % 12) + 1;
  const venLon   = sidLon(Astronomy.Body.Venus);
  const venH     = ((Math.floor(venLon / 30) + 1 - ascendantSign + 12) % 12) + 1;
  const merLon   = sidLon(Astronomy.Body.Mercury);
  const merH     = ((Math.floor(merLon / 30) + 1 - ascendantSign + 12) % 12) + 1;
  const sunH     = ((Math.floor(sunLon / 30) + 1 - ascendantSign + 12) % 12) + 1;

  switch (act) {
    case 'business_start':
      // Mars: initiative (business); Saturn: service/karma (career) — both relevant
      if ([1, 3, 10, 11].includes(marsH)) { score += 6; factors.push(`Mars in ${getHouseName(marsH)} (initiative/drive)`); }
      if ([3, 10, 11, 2].includes(merH))  { score += 6; factors.push(`Mercury in ${getHouseName(merH)} (commerce/skills)`); }
      if ([1, 6, 9, 10].includes(sunH))   { score += 6; factors.push(`Sun in ${getHouseName(sunH)} (authority/status)`); }
      // Saturn transiting H10 or H6 activates career/profession directly
      if ([6, 10, 11].includes(satHouse)) { score += 5; factors.push(`Saturn in ${getHouseName(satHouse)} (career activation)`); }
      break;
    case 'property_purchase':
    case 'graha_pravesh':
      if ([1,4,11].includes(marsH)) { score += 8; factors.push(`Mars in ${getHouseName(marsH)} (property action)`); }
      if ([4,7,11].includes(venH)) { score += 7; factors.push(`Venus in ${getHouseName(venH)} (property pleasure)`); }
      break;
    case 'vehicle_purchase':
      if ([4,11].includes(venH)) { score += 8; factors.push(`Venus in ${getHouseName(venH)} (luxury)`); }
      if ([4,11].includes(marsH)) { score += 6; factors.push(`Mars in ${getHouseName(marsH)} (vehicle energy)`); }
      break;
    case 'ivf_planning':
    case 'cesarean':
      if ([5,7].includes(venH)) { score += 10; factors.push(`Venus in ${getHouseName(venH)} (fertility)`); }
      if (act === 'cesarean' && [5,1].includes(marsH)) { score += 5; factors.push(`Mars in ${getHouseName(marsH)} (delivery)`); }
      break;
    case 'surgery':
      if ([1,6,8].includes(marsH)) { score += 8; factors.push(`Mars in ${getHouseName(marsH)} (surgical energy)`); }
      if ([1,9].includes(sunH)) { score += 6; factors.push(`Sun in ${getHouseName(sunH)} (vitality)`); }
      break;
    case 'investment':
      if ([2,11,9].includes(merH)) { score += 8; factors.push(`Mercury in ${getHouseName(merH)} (financial intelligence)`); }
      if ([2,11,5].includes(venH)) { score += 6; factors.push(`Venus in ${getHouseName(venH)} (wealth)`); }
      break;
    case 'foreign_travel':
      if ([9,12,3].includes(merH)) { score += 6; factors.push(`Mercury in ${getHouseName(merH)} (travel comms)`); }
      if ([9,12].includes(sunH)) { score += 7; factors.push(`Sun in ${getHouseName(sunH)} (foreign opportunity)`); }
      break;
    case 'marriage':
      if ([7,5,11].includes(venH)) { score += 10; factors.push(`Venus in ${getHouseName(venH)} (romance)`); }
      if ([7,1,5].includes(marsH)) { score += 7; factors.push(`Mars in ${getHouseName(marsH)} (attraction)`); }
      break;
  }

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    factors,
  };
}

// ─────────────────────────────────────────────────
// COMBINED PROBABILITY
// ─────────────────────────────────────────────────

export function calculateCombinedProbability(
  natalScore: number,
  dashaScore: number,
  transitScore: number,
): { probability: number; rating: 'Low' | 'Medium' | 'High' | 'Very High' } {
  // Rating thresholds recalibrated for base=0 scoring across all three components.
  // With earned-only scoring, top charts realistically reach 55-75% combined.
  // Thresholds reflect astrological realism, not percentage completeness:
  //   Very High (≥60): Strong house lord in AD + good transits + strong natal
  //   High     (≥45): Multiple supporting factors across dasha + natal
  //   Medium   (≥28): Some dasha activation, weak natal support
  //   Low      (<28): Unrelated dasha, no activation
  const probability = Math.round(natalScore * 0.35 + dashaScore * 0.45 + transitScore * 0.20);
  const rating: 'Low' | 'Medium' | 'High' | 'Very High' =
    probability >= 60 ? 'Very High'
    : probability >= 45 ? 'High'
    : probability >= 28 ? 'Medium'
    : 'Low';
  return { probability, rating };
}

// Re-export for widget use
export { ACTIVITY_HOUSES };
